import { CONSTANTS, UPDATE_SOURCES, GET_SOURCES } from './constants';
import { DebtProductRowData, VisibilityStates } from '../../modules/DebtProducts/debtProductTypes';
import { getLenderKey } from './selectors';

export interface NormalizedProduct extends Omit<DebtProductRowData, 'lenders'> {
    lenderIds: string[]; // Array of composite keys: `${lenderId}_${productId}`
}

export interface NormalizedLender extends DebtProductRowData {
    parentProductId: string;
}

export interface DebtProductsState {
    productsById: Record<string, NormalizedProduct>;
    lendersById: Record<string, NormalizedLender>;
    productOrder: string[];

    headers: Array<{ id: string; label: string }>;
    visibilityStates: VisibilityStates;
}

const initialState: DebtProductsState = {
    productsById: {},
    lendersById: {},
    productOrder: [],
    headers: [],
    visibilityStates: {
        debtProductName: 'Debt Products',
    },
};

/**
 * Normalizes raw API data into the normalized state structure
 */
function normalizeRows(rows: DebtProductRowData[]): {
    productsById: Record<string, NormalizedProduct>;
    lendersById: Record<string, NormalizedLender>;
    productOrder: string[];
} {
    const productsById: Record<string, NormalizedProduct> = {};
    const lendersById: Record<string, NormalizedLender> = {};
    const productOrder: string[] = [];

    rows.forEach((row) => {
        const { lenders, ...productData } = row;
        const productId = row.productId;

        // Create lender IDs array and normalize lenders
        const lenderIds: string[] = [];
        if (lenders && Array.isArray(lenders)) {
            lenders.forEach((lender) => {
                if (lender.lenderId) {
                    const lenderKey = getLenderKey(lender.lenderId, productId);
                    lenderIds.push(lenderKey);
                    lendersById[lenderKey] = {
                        ...lender,
                        parentProductId: productId,
                    };
                }
            });
        }

        // Store normalized product
        productsById[productId] = {
            ...productData,
            lenderIds,
        };

        productOrder.push(productId);
    });

    return { productsById, lendersById, productOrder };
}

const getHeaders = (
    state: DebtProductsState,
    data: { headers: DebtProductsState['headers']; visibilityStates: DebtProductsState['visibilityStates'] },
): DebtProductsState => {
    return {
        ...state,
        headers: data.headers,
        visibilityStates: {
            ...state.visibilityStates,
            ...data.visibilityStates,
        },
    };
};

const getRows = (state: DebtProductsState, data: DebtProductRowData[]): DebtProductsState => {
    const { productsById, lendersById, productOrder } = normalizeRows(data);
    return {
        ...state,
        productsById,
        lendersById,
        productOrder,
    };
};

const updateProduct = (state: DebtProductsState, data: { productId: string; updates: Partial<NormalizedProduct> }): DebtProductsState => {
    const { productId, updates } = data;
    if (!state.productsById[productId]) return state;

    return {
        ...state,
        productsById: {
            ...state.productsById,
            [productId]: {
                ...state.productsById[productId],
                ...updates,
            },
        },
    };
};

const updateLender = (
    state: DebtProductsState,
    data: { lenderId: string; productId: string; updates: Partial<NormalizedLender> },
): DebtProductsState => {
    const { lenderId, productId, updates } = data;
    const lenderKey = getLenderKey(lenderId, productId);
    if (!state.lendersById[lenderKey]) return state;

    return {
        ...state,
        lendersById: {
            ...state.lendersById,
            [lenderKey]: {
                ...state.lendersById[lenderKey],
                ...updates,
            },
        },
    };
};

const toggleProductExpand = (state: DebtProductsState, data: { productId: string }): DebtProductsState => {
    const { productId } = data;
    if (!state.productsById[productId]) return state;

    return {
        ...state,
        productsById: {
            ...state.productsById,
            [productId]: {
                ...state.productsById[productId],
                expand: !state.productsById[productId].expand,
            },
        },
    };
};

const batchUpdateProducts = (
    state: DebtProductsState,
    data: Array<{ productId: string; updates: Partial<NormalizedProduct> }>,
): DebtProductsState => {
    const newProductsById = { ...state.productsById };

    data.forEach(({ productId, updates }) => {
        if (newProductsById[productId]) {
            newProductsById[productId] = {
                ...newProductsById[productId],
                ...updates,
            };
        }
    });

    return {
        ...state,
        productsById: newProductsById,
    };
};

const batchUpdateLenders = (
    state: DebtProductsState,
    data: Array<{ lenderId: string; productId: string; updates: Partial<NormalizedLender> }>,
): DebtProductsState => {
    const newLendersById = { ...state.lendersById };

    data.forEach(({ lenderId, productId, updates }) => {
        const lenderKey = getLenderKey(lenderId, productId);
        if (newLendersById[lenderKey]) {
            newLendersById[lenderKey] = {
                ...newLendersById[lenderKey],
                ...updates,
            };
        }
    });

    return {
        ...state,
        lendersById: newLendersById,
    };
};

const restoreDebtProductsState = (
    state: DebtProductsState,
    data: { productsById: Record<string, NormalizedProduct>; lendersById: Record<string, NormalizedLender> },
): DebtProductsState => {
    return {
        ...state,
        productsById: data.productsById,
        lendersById: data.lendersById,
    };
};

/**
 * Legacy handler: Update expanded row (backward compatibility)
 * Maps to the new updateProduct handler
 */
const updateExpandedRow = (
    state: DebtProductsState,
    data: { productId: string; updates: Partial<DebtProductRowData> },
): DebtProductsState => {
    return updateProduct(state, {
        productId: data.productId,
        updates: data.updates as Partial<NormalizedProduct>,
    });
};

// Handler mappings
const getHandlers: Record<string, (state: DebtProductsState, data: any) => DebtProductsState> = {
    [GET_SOURCES.GET_HEADERS]: getHeaders,
    [GET_SOURCES.GET_ROWS]: getRows,
};

const updateHandlers: Record<string, (state: DebtProductsState, data: any) => DebtProductsState> = {
    [UPDATE_SOURCES.UPDATE_EXPANDED_ROW]: updateExpandedRow,
    [UPDATE_SOURCES.UPDATE_PRODUCT_FIELD]: updateProduct,
    [UPDATE_SOURCES.UPDATE_LENDER_FIELD]: updateLender,
};

function updateDebtProductsData(state: DebtProductsState, action: { payload: { source: string; data: any } }): DebtProductsState {
    const { source, data } = action.payload;
    const handler = updateHandlers[source];
    if (handler) {
        return handler(state, data);
    }
    return state;
}

function getDebtProductsData(state: DebtProductsState, action: { payload: { source: string; data: any } }): DebtProductsState {
    const { source, data } = action.payload;
    const handler = getHandlers[source];
    if (handler) {
        return handler(state, data);
    }
    return state;
}

/**
 * Reorder lenders for a product so table order matches desired order.
 * Lenders in lenderIdsInOrder get that order; others (e.g. shared) keep current position.
 */
const reorderProductLenders = (state: DebtProductsState, data: { productId: string; lenderIdsInOrder: string[] }): DebtProductsState => {
    const { productId, lenderIdsInOrder } = data;
    const product = state.productsById[productId];
    if (!product?.lenderIds?.length) return state;

    const reorderKeySet = new Set(lenderIdsInOrder.map((id) => getLenderKey(id, productId)));
    const otherKeys = product.lenderIds.filter((k) => !reorderKeySet.has(k));
    const keysInOrder = lenderIdsInOrder.map((id) => getLenderKey(id, productId)).filter((k) => state.lendersById[k]);

    return {
        ...state,
        productsById: {
            ...state.productsById,
            [productId]: {
                ...product,
                lenderIds: [...otherKeys, ...keysInOrder],
            },
        },
    };
};

const reducer = (state: DebtProductsState = initialState, action: any): DebtProductsState => {
    switch (action.type) {
        case CONSTANTS.UPDATE_DEBT_PRODUCTS_DATA:
            return updateDebtProductsData(state, action);

        case CONSTANTS.GET_DEBT_PRODUCTS_DATA:
            return getDebtProductsData(state, action);

        case CONSTANTS.UPDATE_PRODUCT:
            return updateProduct(state, action.payload);

        case CONSTANTS.UPDATE_LENDER:
            return updateLender(state, action.payload);

        case CONSTANTS.TOGGLE_PRODUCT_EXPAND:
            return toggleProductExpand(state, action.payload);

        case CONSTANTS.BATCH_UPDATE_PRODUCTS:
            return batchUpdateProducts(state, action.payload);

        case CONSTANTS.BATCH_UPDATE_LENDERS:
            return batchUpdateLenders(state, action.payload);

        case CONSTANTS.RESTORE_DEBT_PRODUCTS_STATE:
            return restoreDebtProductsState(state, action.payload);

        case CONSTANTS.REORDER_PRODUCT_LENDERS:
            return reorderProductLenders(state, action.payload);

        default:
            return state;
    }
};

export default reducer;
